import * as React from "react";
import { Link, type HeadFC, type PageProps } from "gatsby";

import Layout from "../components/layout";
import Email from "../components/Email";
import Seo from "../components/seo";
import Hero from "../components/Hero";
import heroDesktop from "../images/not-found/Hero.png";
import heroMobile from "../images/not-found/Hero-mobile.png";
import Header from "../components/Header";
import { IsiProvider } from "../context/IsiContext";
import IconBackground from "../components/icons/taking-nuplazid-icon.svg";
import IconBackground2 from "../components/icons/acadia-connect-icon.svg";
import Isi from "../components/Isi";
import ArrowDownIcon from "../components/icons/down-arrow-icon.svg";
import ExpandCollapseArrow from "../components/icons/Expand-Collapse-Arrow.svg";
import SectionTwoButtons from "../components/SectionTwoButtons";

const sectionTwoIcons = [IconBackground, IconBackground2];
const sectionTwoTexts = [
  <>
  Learn more about <br/>NUPLAZID
  </>,
  <>
  Get personalized <br/>support from Acadia <br/>Connect<sup>&reg;</sup></>,
];
const sectionTwoButtonLinks = ["/about-nuplazid/", "/acadia-connect-patient-support/"];
const sectionTwoButtonAltTexts = [
  "Taking Nuplazid icon",
  "Acadia connect icon",
];

const scrollToIsi = (event?: React.MouseEvent<HTMLAnchorElement>) => {
  // The link uses href="" which would otherwise reload the page and abort the scroll.
  event?.preventDefault();
  // Animate to the #full-isi-content section, re-targeting on every frame so
  // dynamic content (e.g., the JotForm iframe finishing layout) can't leave
  // us short.
  const prefersReducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const getTargetY = () => {
    const el = document.getElementById("full-isi-content");
    if (!el) return null;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - globalThis.innerHeight);
    const elementTop = el.getBoundingClientRect().top + globalThis.scrollY;
    return Math.min(elementTop, maxScroll);
  };

  const initialTarget = getTargetY();
  if (initialTarget === null) return;

  if (prefersReducedMotion) {
    globalThis.scrollTo(0, initialTarget);
    return;
  }

  const startY = globalThis.scrollY;
  const startTime = performance.now();
  const duration = 600; // ms

  // easeInOutCubic
  const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const step = (now: number) => {
    const elapsed = Math.min(1, (now - startTime) / duration);
    const target = getTargetY() ?? initialTarget; // re-read each frame
    const next = startY + (target - startY) * ease(elapsed);
    globalThis.scrollTo(0, next);
    if (elapsed < 1) {
      globalThis.requestAnimationFrame(step);
    } else {
      // Final settle in case the page grew on the very last frame.
      globalThis.scrollTo(0, getTargetY() ?? initialTarget);
    }
  };

  globalThis.requestAnimationFrame(step);
};

const accordionItems = [
  {
    title:
      "Are hallucinations and delusions common in people with Parkinson’s?",
    content: (
      <>
        <ul>
          <li>
            Yes. About 50% of people with Parkinson’s may experience{" "}
            <a target="_blank" href="/pdf/pd-related-hallucinations-delusions-fact-sheet_new.pdf">
              hallucinations and/or delusions
            </a>{" "}
            over the course of their disease
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "What are hallucinations and delusions?",
    content: (
      <>
        <ul>
          <li>
            A hallucination means seeing, hearing, or experiencing things that aren’t real
          </li>
          <li>A delusion means believing things that aren’t true</li>
        </ul>
        <p>
          Learn more about Parkinson’s-related hallucinations and delusions from our <a href="/pdf/pd-related-hallucinations-delusions-fact-sheet_new.pdf" target="_blank" className="underline">hallucinations and delusions fact sheet</a>.
        </p>
      </>
    ),
  },
  {
    title: "How do people describe hallucinations and delusions?",
    content: (
      <>
        <ul>
          <li className="mb-24">
            Hallucinations may be described as:
            <ul>
              <li>
                Seeing things that aren’t real, such as people, either living or deceased, animals, or objects
              </li>
              <li>
                Hearing things that aren’t real, such as sounds, music, or voices
              </li>
              <li>Smelling things that aren’t there, like unusual odors</li>
              <li>
                Feeling something that isn’t there, like something touching or moving on the skin
              </li>
              <li>
                Tasting something that isn’t there, like experiencing an unusual taste in the mouth
              </li>
            </ul>
          </li>

          <li>
            Delusions may be described as:
            <ul>
              <li>
                Paranoia, such as believing people are deceiving you or trying to access your money
              </li>
              <li>
                False beliefs, such as fears of your loved ones stealing from you, or loved ones putting you in harm’s way
              </li>
              <li>
                Jealousy, such as believing a significant other is having an affair
              </li>
              <li>
                Beliefs that insignificant remarks or statements refer to you (eg, walking into a room of people laughing and assuming they are laughing at you)
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "What causes hallucinations and delusions?",
    content: (
      <>
        <ul>
          <li>
            Currently there is no clear understanding of the exact cause of Parkinson’s‑related hallucinations and delusions
          </li>
          <li>
            However, these symptoms are thought to be caused by the following:
            <ul>
              <li>
                A side effect of dopamine therapy. Hallucinations and delusions may be a side effect of common Parkinson’s medications (called dopaminergic therapies). These medications increase dopamine levels in the brain, helping improve motor symptoms in patients with Parkinson’s. However, increasing dopamine levels may also cause changes that lead to hallucinations and delusions
              </li>
              <li>
                The natural progression of Parkinson’s. Hallucinations and delusions may be caused by changes in the brain that occur naturally as Parkinson’s progresses—regardless of whether or not you take any medications to increase your dopamine levels
              </li>
            </ul>
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "When do hallucinations and delusions typically occur?",
    content: (
      <>
        <ul>
          <li>
            Parkinson’s-related hallucinations and/or delusions can occur at any stage of Parkinson’s disease
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Should I be worried about hallucinations and delusions?",
    content: (
      <>
        <ul>
          <li>
            It’s important to speak to a healthcare provider if you or your loved one think you may be experiencing hallucinations or delusions associated with Parkinson’s disease
          </li>
          <li>
            Parkinson’s-related hallucinations and delusions may get worse over time, and people who experience these symptoms may not have the ability to identify whether or not what they’re experiencing is real
          </li>
        </ul>
      </>
    ),
  },
  {
    title:
      "How do I help my loved one who may be experiencing hallucinations and delusions?",
    content: (
      <>
        <ul>
          <li>
            You may use our <a href="/pdf/symptom-discussion-guide.pdf" target="_blank" className="underline">Symptom Discussion Guide</a> to help describe the symptoms they are experiencing to a healthcare provider
          </li>
          <li>
            You may use our <a href="/pdf/Doctor-Discussion-Guide.pdf" target="_blank" className="underline">Doctor Discussion Guide</a> to help you prepare for a conversation with their healthcare provider about treatment with NUPLAZID
          </li>
        </ul>
      </>
    ),
  },
];

const accordionItems2 = [
  {
    title: "What is NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            <Link
              to="/about-nuplazid/"
              className="underline text-royal-blue QuireSanProBold"
            >
              NUPLAZID
            </Link>{" "}
             is a prescription medicine used to treat hallucinations (such as seeing, hearing, or experiencing things that aren’t real) and delusions (such as believing things that aren’t true) associated with Parkinson’s
          </li>
        </ul>
      </>
    ),
  },
  {
    title:
      "What is the most important information I should know about the safety profile of NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            Medicines like NUPLAZID can raise the risk of death in elderly people who have lost touch with reality (psychosis) due to confusion and memory loss (dementia)
          </li>
          <li>
            NUPLAZID is not approved for the treatment of patients with dementia who experience psychosis unless their hallucinations and delusions are related to Parkinson’s
          </li>
        </ul>
        <p>
          View additional{" "}
          <a href=""
            className="underline text-royal-blue QuireSanProBold cursor-pointer"
            onClick={scrollToIsi}
          >
            safety information
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: "How can NUPLAZID help me?",
    content: (
      <>
        <ul>
          <li>
            NUPLAZID may help to reduce the frequency and/or the severity of hallucinations and/or delusions associated with Parkinson’s. Individual results may vary
          </li>
        </ul>
        <p>
          Learn more about how NUPLAZID could help you from our{" "}
          <a href="/pdf/nuplazid-fact-sheet.pdf" target="_blank" className="underline">NUPLAZID fact sheet</a>.
        </p>
      </>
    ),
  },
  {
    title:
      "What if I only experience hallucinations or delusions and not both symptoms?",
    content: (
      <>
        <ul>
          <li>
            Speak to your doctor to learn if NUPLAZID is right for you. NUPLAZID may help to reduce the frequency and/or the severity of hallucinations and/or delusions associated with Parkinson’s. Individual results may vary
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How effective is NUPLAZID?",
    content: (
      <>
        <ul className="pb-[8px]">
          <li>
            In the clinical trial, the majority of patients who took NUPLAZID experienced fewer and/or less severe hallucinations and/or delusions when compared to placebo. And some patients did not experience any hallucinations or delusions at 6 weeks. Not everyone will respond to NUPLAZID. Individual results may vary
          </li>
          <li>
            NUPLAZID may begin to reduce hallucinations and/or delusions as soon as 4 weeks, but it may take up to 6 weeks to feel the full effect. You should speak with your healthcare provider before you stop taking NUPLAZID
          </li>
        </ul>
        <Link className="pb-[24px]" to="/about-nuplazid/">See more about the trial.</Link>
      </>
    ),
  },
  {
    title: "Will NUPLAZID affect my Parkinson’s‑related movement issues?",
    content: (
      <>
        <ul>
          <li>
            In the clinical trial, NUPLAZID had no effect on motor symptoms compared with a placebo at 6 weeks
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How does NUPLAZID work?",
    content: (
      <>
        <ul>
          <li>It is unclear how NUPLAZID actually works</li>
          <li>
            NUPLAZID is thought to work by blocking certain receptors associated with hallucinations and delusions
          </li>
        </ul>
      </>
    ),
  },
  {
    title:
      "When was NUPLAZID FDA-approved for hallucinations and delusions associated with Parkinson’s?",
    content: (
      <>
        <ul>
          <li>NUPLAZID was approved by the FDA in 2016</li>
        </ul>
      </>
    ),
  },
  {
    title: "Who shouldn’t take NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            Do not take NUPLAZID if you have had an allergic reaction to any of the ingredients in NUPLAZID. Allergic reactions have included rash, hives, swelling of the tongue, mouth, lips, or face, throat tightness, and shortness of breath. Speak with your doctor to find out if NUPLAZID is right for you
          </li>
        </ul>
      </>
    ),
  },
];

const accordionItems3 = [
  {
    title: "How do I take NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            The recommended dose of NUPLAZID is one 34 mg capsule, once daily by mouth, with or without food, taken around the same time every day, or as prescribed by your healthcare provider
          </li>
        </ul>
      </>
    ),
  },
  {
    title:
      "Can I take NUPLAZID with carbidopa/levodopa medicines I’m taking for Parkinson’s?",
    content: (
      <>
        <ul>
          <li>
            NUPLAZID can be taken without adjusting carbidopa/levodopa medicines. Some medicines should not be taken with NUPLAZID. Tell your healthcare provider about all of the medicines you are taking
          </li>
        </ul>
        <p>
          Learn more about{" "}
          <Link to="/about-nuplazid/" className="underline">
            taking NUPLAZID
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    title: "How long does it take for NUPLAZID to start working?",
    content: (
      <>
        <ul>
          <li>
            NUPLAZID may begin to reduce hallucinations and/or delusions as soon as 4 weeks, but it may take up to 6 weeks to feel the full effect. You should speak with your healthcare provider before you stop taking NUPLAZID
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "What medicines might interact with NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            Tell your healthcare provider about all the medicines you take. Other medicines may affect how NUPLAZID works. Some medicines should not be taken with NUPLAZID. Your healthcare provider can tell you if it is safe to take NUPLAZID with your other medicines. Do not start or stop any medicines while taking NUPLAZID without talking to your healthcare provider first
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "What warnings should I know about NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            QT Interval Prolongation: NUPLAZID may increase the risk of changes to your heart rhythm. This risk may increase if NUPLAZID is taken with certain other medications known to prolong the QT interval. Tell your healthcare provider about all the medicines you take or have recently taken
          </li>
          <li>
            Do not take NUPLAZID if you have certain heart conditions that change your heart rhythm. It is important to talk to your healthcare provider about this possible side effect. Call your healthcare provider if you feel a change in your heartbeat
          </li>
        </ul>
        <p>
          View additional{" "}
          <a href=""
            className="underline text-royal-blue QuireSanProBold cursor-pointer"
            onClick={scrollToIsi}
          >
            safety information
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: "What are the possible side effects of NUPLAZID?",
    content: (
      <>
        <p>The common side effects of NUPLAZID include</p>
        <ul>
          <li>Swelling in the legs or arms</li>
          <li>Nausea</li>
          <li>Confusion</li>
          <li>Hallucination</li>
          <li>Constipation</li>
          <li>Changes to normal walking</li>
        </ul>
        <p>
          These are not all the possible side effects of NUPLAZID. For more information, ask your healthcare provider or pharmacist about this medicine.
        </p>
      </>
    ),
  },
];

const accordionItems4 = [
  {
    title: "How do I get NUPLAZID?",
    content: (
      <>
        <ul>
          <li>
            Acadia Connect® can coordinate with you and the pharmacy filling the prescription to help get the medicine delivered wherever is most convenient to you
          </li>
        </ul>
        <a
          href="/acadia-connect-patient-support"
        >
          Learn more about Acadia Connect
        </a>
      </>
    ),
  },
  {
    title: "How much does NUPLAZID cost?",
    content: (
      <>
        <ul>
          <li>
            There are options available that may help you cover the cost of NUPLAZID. Visit{" "}
            <a
              href="/acadia-connect-patient-support"
            >
              Acadia Connect
            </a>{" "}
            to learn more
          </li>
        </ul>
      </>
    ),
  },
];

const MAX_IFRAME_HEIGHT = 50000;
const MOBILE_BREAKPOINT = 640;
const MOBILE_SUBMITTED_IFRAME_HEIGHT = 430;
const DESKTOP_SUBMITTED_IFRAME_HEIGHT = 190;
const MIN_THANK_YOU_HEIGHT = 120;

const extractFromJotFormObject = (obj: Record<string, unknown>): { action: string; height: number | null } => {
  const action = typeof obj.action === "string" ? obj.action : "";
  const args = obj.args;
  let height: number | null = null;

  if (Array.isArray(args)) {
    // args may be [height] or [formId, height] — form IDs are enormous numbers,
    // so find the last element that parses as a plausible page height.
    for (const item of args) {
      const val = Number(item);
      if (!Number.isNaN(val) && val > 0 && val < MAX_IFRAME_HEIGHT) {
        height = val;
      }
    }
  } else {
    const raw = (args as Record<string, unknown> | undefined)?.height;
    const val = Number(raw);
    height = !Number.isNaN(val) && val > 0 && val < MAX_IFRAME_HEIGHT ? val : null;
  }

  return { action, height };
};

const parseJotFormMessage = (data: unknown): { action: string; height: number | null } => {
  if (typeof data === "string") {
    try {
      return extractFromJotFormObject(JSON.parse(data) as Record<string, unknown>);
    } catch {
      const parts = data.split(":");
      if (parts.length === 1) {
        return { action: data, height: null };
      }

      const action = parts[0];
      let height: number | null = null;
      // Accept both "setHeight:HEIGHT:FORMID" and "setHeight:FORMID:HEIGHT"
      // by selecting the first plausible height value.
      for (const segment of parts.slice(1)) {
        const val = Number(segment);
        if (!Number.isNaN(val) && val > 0 && val < MAX_IFRAME_HEIGHT) {
          height = val;
          break;
        }
      }

      return { action, height };
    }
  }
  if (data !== null && typeof data === "object") {
    return extractFromJotFormObject(data as Record<string, unknown>);
  }
  return { action: "", height: null };
};

const extractHeightFromPayload = (payload: unknown): number | null => {
  const raw = typeof payload === "string" ? payload : JSON.stringify(payload ?? "");
  const numbers = raw.match(/\d+/g);
  if (!numbers) {
    return null;
  }

  const candidates = numbers
    .map(Number)
    .filter((val) => !Number.isNaN(val) && val > 50 && val < MAX_IFRAME_HEIGHT && val < 5000);

  if (candidates.length === 0) {
    return null;
  }

  return candidates[candidates.length - 1] ?? null;
};

const FaqPage: React.FC<PageProps> = ({ location }) => {
  const iframeLoadCount = React.useRef(0);
  const formSubmittedRef = React.useRef(false);
  const pendingThankYouHeightRef = React.useRef(false);
  const thankYouFallbackTimerRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const [iframeHeight, setIframeHeight] = React.useState("2330px");
  const [iframeHeightMobile, setIframeHeightMobile] = React.useState("3890px");
  const [openItems, setOpenItems] = React.useState<boolean[]>(
    accordionItems.map(() => false),
  );
  const [openItems2, setOpenItems2] = React.useState<boolean[]>(
    accordionItems2.map(() => false),
  );
  const [openItems3, setOpenItems3] = React.useState<boolean[]>(
    accordionItems3.map(() => false),
  );
  const [openItems4, setOpenItems4] = React.useState<boolean[]>(
    accordionItems4.map(() => false),
  );

  const toggleAccordionItem = (index: number) => {
    setOpenItems((prev) =>
      prev.map((isOpen, itemIndex) => (itemIndex === index ? !isOpen : isOpen)),
    );
  };

  const toggleAccordionItem2 = (index: number) => {
    setOpenItems2((prev) =>
      prev.map((isOpen, itemIndex) => (itemIndex === index ? !isOpen : isOpen)),
    );
  };

  const toggleAccordionItem3 = (index: number) => {
    setOpenItems3((prev) =>
      prev.map((isOpen, itemIndex) => (itemIndex === index ? !isOpen : isOpen)),
    );
  };

  const toggleAccordionItem4 = (index: number) => {
    setOpenItems4((prev) =>
      prev.map((isOpen, itemIndex) => (itemIndex === index ? !isOpen : isOpen)),
    );
  };

  const expandAll = () => {
    setOpenItems(accordionItems.map(() => true));
    setOpenItems2(accordionItems2.map(() => true));
    setOpenItems3(accordionItems3.map(() => true));
    setOpenItems4(accordionItems4.map(() => true));
  };

  const collapseAll = () => {
    setOpenItems(accordionItems.map(() => false));
    setOpenItems2(accordionItems2.map(() => false));
    setOpenItems3(accordionItems3.map(() => false));
    setOpenItems4(accordionItems4.map(() => false));
  };

  const allExpanded =
    openItems.every((v) => v) &&
    openItems2.every((v) => v) &&
    openItems3.every((v) => v) &&
    openItems4.every((v) => v);

  const allCollapsed =
    openItems.every((v) => !v) &&
    openItems2.every((v) => !v) &&
    openItems3.every((v) => !v) &&
    openItems4.every((v) => !v);

  const setSubmittedIframeHeight = React.useCallback(() => {
    if (globalThis.window !== undefined && globalThis.window.innerWidth <= MOBILE_BREAKPOINT) {
      setIframeHeightMobile(`${MOBILE_SUBMITTED_IFRAME_HEIGHT}px`);
      return;
    }

    setIframeHeight(`${DESKTOP_SUBMITTED_IFRAME_HEIGHT}px`);
  }, []);

  const setDesktopOrMobileHeight = React.useCallback((pxHeight: number) => {
    const cssHeight = `${pxHeight}px`;
    if (globalThis.window !== undefined && globalThis.window.innerWidth <= MOBILE_BREAKPOINT) {
      setIframeHeightMobile(cssHeight);
      return;
    }

    setIframeHeight(cssHeight);
  }, []);

  const scheduleThankYouFallbackHeight = React.useCallback(() => {
    if (thankYouFallbackTimerRef.current !== null) {
      globalThis.clearTimeout(thankYouFallbackTimerRef.current);
    }

    thankYouFallbackTimerRef.current = globalThis.setTimeout(() => {
      if (!pendingThankYouHeightRef.current) {
        return;
      }

      setSubmittedIframeHeight();
      pendingThankYouHeightRef.current = false;
    }, 800);
  }, [setSubmittedIframeHeight]);

  React.useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.origin.includes("jotform")) {
        return;
      }

      const iframe = document.getElementById("JotFormIFrame-262215310446851") as HTMLIFrameElement | null;
      if (!iframe) {
        return;
      }

      const { action, height } = parseJotFormMessage(e.data);
      const normalizedAction = action.toLowerCase();
      const normalizedPayload = (typeof e.data === "string" ? e.data : JSON.stringify(e.data ?? "")).toLowerCase();

      // Check all known JotForm submission action variants.
      const isSubmissionAction =
        normalizedAction === "setsubmit" ||
        normalizedAction === "submission" ||
        normalizedAction === "submission-completed" ||
        normalizedAction.includes("submitted") ||
        normalizedAction.includes("submitcomplete") ||
        normalizedAction.includes("thankyou") ||
        normalizedPayload.includes("thankyou") ||
        normalizedPayload.includes("thank you") ||
        normalizedPayload.includes("submitted") ||
        normalizedPayload.includes("submission-completed") ||
        normalizedPayload.includes("submitcomplete");

      if (isSubmissionAction) {
        formSubmittedRef.current = true;
        pendingThankYouHeightRef.current = true;
        scheduleThankYouFallbackHeight();
        window.scrollTo({ top: iframe.getBoundingClientRect().top + window.scrollY - 210, behavior: "smooth" });
      }

      const isSetHeightEvent = normalizedAction.includes("setheight") || normalizedPayload.includes("setheight");
      const resolvedHeight = height ?? extractHeightFromPayload(e.data);

      if (isSetHeightEvent && resolvedHeight !== null && resolvedHeight > 0) {
        if (pendingThankYouHeightRef.current || formSubmittedRef.current || iframeLoadCount.current > 1) {
          const isMobileViewport = globalThis.window !== undefined && globalThis.window.innerWidth <= MOBILE_BREAKPOINT;
          const measuredHeight = Math.max(MIN_THANK_YOU_HEIGHT, resolvedHeight);
          const nextHeight = isMobileViewport
            ? Math.min(MOBILE_SUBMITTED_IFRAME_HEIGHT, measuredHeight)
            : measuredHeight;
          setDesktopOrMobileHeight(nextHeight);
          pendingThankYouHeightRef.current = false;
          if (thankYouFallbackTimerRef.current !== null) {
            globalThis.clearTimeout(thankYouFallbackTimerRef.current);
            thankYouFallbackTimerRef.current = null;
          }
        }
      }

      // JotForm sends scrollIntoView specifically after submission to bring the
      // thank-you message into view. Use it as an additional submission signal.
      if (normalizedAction.includes("scrollintoview") || normalizedPayload.includes("scrollintoview")) {
        if (formSubmittedRef.current) {
          window.scrollTo({ top: iframe.getBoundingClientRect().top + window.scrollY - 210, behavior: "smooth" });
        } else {
          iframe.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    window.addEventListener("message", handleMessage, false);
    return () => {
      if (thankYouFallbackTimerRef.current !== null) {
        globalThis.clearTimeout(thankYouFallbackTimerRef.current);
      }
      window.removeEventListener("message", handleMessage, false);
    };
  }, [scheduleThankYouFallbackHeight, setDesktopOrMobileHeight]);

  return (
    <IsiProvider>
      <Header location={location} />
      <Layout>
        <div className="container">
          <div className="row">
            <div className="columns text-center mx-0 px-0">
              <Hero
                heading={<>Frequently Asked <br className="mobile"/>Questions</>}
                linkHref=""
                backgroundImage={heroDesktop}
                backgroundImageMobile={heroMobile}
                className="md_mx-0 mx-1"
                faqPage={true}
                noHeroMobile={true}
              />
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row lg_mt-4 mt-2-5">
            <div className="m-auto columns small-12 large-10">
              <p className="columns small-12 large-10 text-black QuireSanProBold mb-24">
                Jump to section:
              </p>
            </div>
            <div className="jump-links m-auto columns small-12 large-10">
              <a className="bg-yellow" href="#parkinsons-related-hallucinations-delusions">
                Parkinson’s-related <br/>hallucinations & delusions
                <img src={ArrowDownIcon} alt="Down arrow icon" />
              </a>
              <a className="bg-yellow" href="#about-nuplazid">
                <span>About <br />NUPLAZID<sup>&reg;</sup></span>
                <img src={ArrowDownIcon} alt="Down arrow icon" />
              </a>
              <a className="bg-yellow" href="#taking-nuplazid">
                Taking <br className="desktop"/>NUPLAZID
                <img src={ArrowDownIcon} alt="Down arrow icon" />
              </a>
              <a className="bg-yellow mb-0" href="#getting-nuplazid">
                Getting <br className="desktop"/>NUPLAZID
                <img src={ArrowDownIcon} alt="Down arrow icon" />
              </a>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row mt-2-5 gap-1">
            <div className="m-auto columns small-12 large-10">
              <div className="accordion-section">
                <div className="mb-1 text-right">
                  <button
                    type="button"
                    className="mr-1"
                    onClick={expandAll}
                    aria-pressed={allExpanded ? "true" : "false"}
                    style={{ fontFamily: allExpanded ? "QuireSanProRegular" : "QuireSanProBold" }}
                  >
                    Expand All
                    <img
                      src={ExpandCollapseArrow}
                      alt=""
                      aria-hidden="true"
                      style={{
                        transform: "rotate(180deg)",
                      }}
                      className="w-unset"
                    />
                  </button>
                  <button
                    type="button"
                    aria-pressed={allExpanded ? "false" : "true"}
                    onClick={collapseAll}
                    style={{ fontFamily: allCollapsed ? "QuireSanProRegular" : "QuireSanProBold" }}
                  >
                    Collapse All
                    <img
                      src={ExpandCollapseArrow}
                      alt=""
                      aria-hidden="true"
                      style={{
                        transform: "rotate(0deg)",
                      }}
                      className="w-unset"
                    />
                  </button>
                </div>
                <h2 className="mt-0 mb-24" id="parkinsons-related-hallucinations-delusions">
                  Parkinson’s-related hallucinations and delusions
                </h2>
                {accordionItems.map((item, index) => {
                  const itemId = `faq-accordion-item-${index}`;
                  const panelId = `faq-accordion-panel-${index}`;
                  const isOpen = openItems[index];

                  return (
                    <div key={itemId} className="mb-1-5 border-bottom-yellow">
                      <button
                        id={itemId}
                        type="button"
                        className="flex text-[28px] w-full text-left"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleAccordionItem(index)}
                      >
                        <div className="small-2 large-1 text-center">
                          <img
                            src={ExpandCollapseArrow}
                            alt=""
                            style={{
                              transform: isOpen
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>
                        <p className="small-10 large-11">{item.title}</p>
                      </button>
                      {isOpen && (
                        <section
                          id={panelId}
                          aria-labelledby={itemId}
                          className="row"
                        >
                          <p className="mb-24 pb-[24px] faq-panel-content">{item.content}</p>
                        </section>
                      )}
                    </div>
                  );
                })}

                <h2 className="mb-24 pt-24" id="about-nuplazid">About NUPLAZID</h2>
                {accordionItems2.map((item, index) => {
                  const itemId = `faq-accordion-item2-${index}`;
                  const panelId = `faq-accordion-panel2-${index}`;
                  const isOpen = openItems2[index];

                  return (
                    <div key={itemId} className="mb-1-5 border-bottom-yellow">
                      <button
                        id={itemId}
                        type="button"
                        className="flex text-[28px] w-full text-left"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleAccordionItem2(index)}
                      >
                        <div className="small-2 large-1 text-center">
                          <img
                            src={ExpandCollapseArrow}
                            alt=""
                            style={{
                              transform: isOpen
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>
                        <p className="small-10 large-11">{item.title}</p>
                      </button>
                      {isOpen && (
                        <section
                          id={panelId}
                          aria-labelledby={itemId}
                          className="row"
                        >
                          <p className="mb-24 pb-[24px] faq-panel-content">{item.content}</p>
                        </section>
                      )}
                    </div>
                  );
                })}

                <h2 className="mb-24 pt-24" id="taking-nuplazid">Taking NUPLAZID</h2>
                {accordionItems3.map((item, index) => {
                  const itemId = `faq-accordion-item3-${index}`;
                  const panelId = `faq-accordion-panel3-${index}`;
                  const isOpen = openItems3[index];

                  return (
                    <div key={itemId} className="mb-1-5 border-bottom-yellow">
                      <button
                        id={itemId}
                        type="button"
                        className="flex text-[28px] w-full text-left"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleAccordionItem3(index)}
                      >
                        <div className="small-2 large-1 text-center">
                          <img
                            src={ExpandCollapseArrow}
                            alt=""
                            style={{
                              transform: isOpen
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>
                        <p className="small-10 large-11">{item.title}</p>
                      </button>
                      {isOpen && (
                        <section
                          id={panelId}
                          aria-labelledby={itemId}
                          className="row"
                        >
                          <p className="mb-24 pb-[24px] faq-panel-content">{item.content}</p>
                        </section>
                      )}
                    </div>
                  );
                })}

                <h2 className="mb-24 pt-24" id="getting-nuplazid">Getting NUPLAZID</h2>
                {accordionItems4.map((item, index) => {
                  const itemId = `faq-accordion-item4-${index}`;
                  const panelId = `faq-accordion-panel4-${index}`;
                  const isOpen = openItems4[index];

                  return (
                    <div key={itemId} className="mb-1-5 border-bottom-yellow">
                      <button
                        id={itemId}
                        type="button"
                        className="flex text-[28px] w-full text-left"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleAccordionItem4(index)}
                      >
                        <div className="small-2 large-1 text-center">
                          <img
                            src={ExpandCollapseArrow}
                            alt=""
                            style={{
                              transform: isOpen
                                ? "rotate(0deg)"
                                : "rotate(180deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>
                        <p className="small-10 large-11">{item.title}</p>
                      </button>
                      {isOpen && (
                        <section
                          id={panelId}
                          aria-labelledby={itemId}
                          className="row"
                        >
                          <p className="mb-24 pb-[24px] faq-panel-content">{item.content}</p>
                        </section>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-primary-100">
          
          <div className="container mt-4 relative" id="patient-educator-tool">
            <div className="row pt-4">
              <div className="columns large-10 m-auto lg_px-0">

                <iframe
                  id="JotFormIFrame-262215310446851"
                  title="Patient Educator Tool - new edition"
                  onLoad={() => {
                    iframeLoadCount.current += 1;
                    if (iframeLoadCount.current > 1) {
                      formSubmittedRef.current = true;
                      pendingThankYouHeightRef.current = true;
                      scheduleThankYouFallbackHeight();
                      const iframe = document.getElementById("JotFormIFrame-262215310446851");
                      if (iframe) {
                        window.scrollTo({ top: iframe.getBoundingClientRect().top + window.scrollY - 210, behavior: "smooth" });
                      }
                    }
                  }}
                  allow="geolocation; microphone; camera; fullscreen; payment"
                  src="https://acadia.jotform.com/262215310446851"
                  style={{minWidth: "100%", maxWidth: "100%", height: globalThis.window !== undefined && globalThis.window.innerWidth <= 640 ? iframeHeightMobile : iframeHeight, border: "none", overflowX: "hidden"}}
                  // style={{ minWidth: '100%', maxWidth: '100%', height: '539px', border: 'none' }}
                  scrolling="no"
                >
                </iframe>
    
              </div>
            </div>
        </div>
        </div>

        <SectionTwoButtons
          icons={sectionTwoIcons}
          texts={sectionTwoTexts}
          buttonLinks={sectionTwoButtonLinks}
          buttonAltTexts={sectionTwoButtonAltTexts}
        />

        <div className="container mt-5">
          <Email />
        </div>

        <Isi position="fixed" />
      </Layout>
    </IsiProvider>
  );
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Are hallucinations and delusions common in people with Parkinson's?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. About 50% of people with Parkinson's may experience hallucinations and/or delusions (https://www.nuplazid.com/pdf/pd-related-hallucinations-delusions-fact-sheet.pdf) over the course of their disease",
      },
    },
    {
      "@type": "Question",
      name: "What are hallucinations and delusions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A hallucination means seeing, hearing, or experiencing things that aren't real.\nA delusion means believing things that aren’t true.\nLearn more about Parkinson's-related hallucinations and delusions from our hallucinations and delusions fact sheet (https://www.nuplazid.com/pdf/pd-related-hallucinations-delusions-fact-sheet.pdf).",
      },
    },
    {
      "@type": "Question",
      name: "How do people describe hallucinations and delusions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Hallucinations may be described as: \n-Seeing things that aren't real, such as people, either living or deceased, animals, or objects \n-Hearing things that aren't real, such as hearing sounds, music, or voices \n-Smelling things that aren't there, like unusual odors \n-Feeling something that isn't there, like something touching or moving on the skin \n-Tasting something that isn't there, like experiencing an unusual taste in the mouth \n\nDelusions may be described as: \n-Paranoia, such as believing people are deceiving you or trying to access your money \n-False beliefs, such as fears of your loved ones stealing from you, or loved ones putting you in harm's way \n-Jealousy, such as believing a significant other is having an affair \n-Beliefs that insignificant remarks or statements refer to you (eg, walking into a room of people laughing and assuming they are laughing at you)",
      },
    },
    {
      "@type": "Question",
      name: "What causes hallucinations and delusions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Currently there is no clear understanding of the exact cause of Parkinson's-related hallucinations and delusions \n\nHowever, these symptoms are thought to be caused by the following: \n\nA side effect of dopamine therapy. Hallucinations and delusions may be a side effect of common Parkinson's medications (called dopaminergic therapies). These medications increase dopamine levels in the brain, helping improve motor symptoms in patients with Parkinson's. However, increasing dopamine levels may also cause changes that lead to hallucinations and delusions \n\nThe natural progression of Parkinson’s. Hallucinations and delusions may be caused by changes in the brain that occur naturally as Parkinson's progresses—regardless of whether or not you take any medications to increase your dopamine levels",
      },
    },
    {
      "@type": "Question",
      name: "When do hallucinations and delusions typically occur?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Parkinson's-related hallucinations and/or delusions can occur at any stage of Parkinson's disease",
      },
    },
    {
      "@type": "Question",
      name: "Should I be worried about hallucinations and delusions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It’s important to speak to a healthcare provider if you or your loved one think you may be experiencing hallucinations or delusions associated with Parkinson’s disease \n\nParkinson's-related hallucinations and delusions may get worse over time, and people who experience these symptoms may not have the ability to identify whether or not what they're experiencing is real",
      },
    },
    {
      "@type": "Question",
      name: "How do I help my loved one who may be experiencing hallucinations or delusions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You may use our  Symptom Discussion Guide (https://www.nuplazid.com/pdf/symptom-discussion-guide.pdf) to help describe the symptoms they are experiencing to a healthcare provider \n\nYou may use our Doctor Discussion Guide (https://www.nuplazid.com/pdf/Doctor-Discussion-Guide.pdf) to help you prepare for a conversation with their healthcare provider about treatment with NUPLAZID",
      },
    },
    {
      "@type": "Question",
      name: "What is NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "NUPLAZID  (https://www.nuplazid.com/about-nuplazid) is a prescription medicine used to treat hallucinations (such as seeing, hearing, or experiencing things that aren't real) and delusions (such as believing things that aren’t true) associated with Parkinson’s",
      },
    },
    {
      "@type": "Question",
      name: "What is the most important information I should know about the safety profile of NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Medicines like NUPLAZID can raise the risk of death in elderly people who have lost touch with reality (psychosis) due to confusion and memory loss (dementia) \n\nNUPLAZID is not approved for the treatment of patients with dementia who experience psychosis unless their hallucinations and delusions are related to Parkinson’s \n\nFor additional safety information, see below (https://www.nuplazid.com/frequently-asked-questions#isi).",
      },
    },
    {
      "@type": "Question",
      name: "How can NUPLAZID help me?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "NUPLAZID may help to reduce the frequency and/or the severity of hallucinations and/or delusions associated with Parkinson's. Individual results may vary \n\nLearn more about how NUPLAZID could help you from our  NUPLAZID fact sheet (https://www.nuplazid.com/pdf/nuplazid-fact-sheet.pdf).",
      },
    },
    {
      "@type": "Question",
      name: "What if I only experience hallucinations or delusions and not both symptoms?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Speak to your doctor to learn if NUPLAZID is right for you. NUPLAZID may help to reduce the frequency and/or the severity of hallucinations and/or delusions associated with Parkinson's. Individual results may vary",
      },
    },
    {
      "@type": "Question",
      name: "How effective is NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "In the clinical trial, the majority of patients who took NUPLAZID experienced fewer and/or less severe hallucinations and/or delusions when compared to placebo. And some patients did not experience any hallucinations or delusions at 6 weeks. Not everyone will respond to NUPLAZID. Individual results may vary \n\nNUPLAZID may begin to reduce hallucinations and/or delusions as soon as 4 weeks, but it may take up to 6 weeks to feel the full effect. You should speak with your healthcare provider before you stop taking NUPLAZID \n\nSee more about the trial. (https://www.nuplazid.com/about-nuplazid)",
      },
    },
    {
      "@type": "Question",
      name: "Will NUPLAZID affect my Parkinson’s-related movement issues?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "In the clinical trial, NUPLAZID had no effect on motor symptoms compared with a placebo at 6 weeks",
      },
    },
    {
      "@type": "Question",
      name: "How does NUPLAZID work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It is unclear how NUPLAZID actually works \n\nNUPLAZID is thought to work by blocking certain receptors associated with hallucinations and delusions",
      },
    },
    {
      "@type": "Question",
      name: "When was NUPLAZID FDA-approved for hallucinations and delusions associated with Parkinson's?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "NUPLAZID was approved by the FDA in 2016",
      },
    },
    {
      "@type": "Question",
      name: "Who shouldn't take NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Do not take NUPLAZID if you have had an allergic reaction to any of the ingredients in NUPLAZID. Allergic reactions have included rash, hives, swelling of the tongue, mouth, lips, or face, throat tightness, and shortness of breath. Speak with your doctor to find out if NUPLAZID is right for you",
      },
    },
    {
      "@type": "Question",
      name: "How do I take NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The recommended dose of NUPLAZID is one 34 mg capsule, once daily by mouth, with or without food, taken around the same time every day, or as prescribed by your healthcare provider",
      },
    },
    {
      "@type": "Question",
      name: "Can I take NUPLAZID with carbidopa/levodopa medicines I’m taking for Parkinson's?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "NUPLAZID can be taken without adjusting carbidopa/levodopa medicines. Some medicines should not be taken with NUPLAZID. Tell your healthcare provider about all of the medicines you are taking \n\nLearn more about taking NUPLAZID (https://www.nuplazid.com/about-nuplazid).",
      },
    },
    {
      "@type": "Question",
      name: "How long does it take for NUPLAZID to start working?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "NUPLAZID may begin to reduce hallucinations and/or delusions as soon as 4 weeks, but it may take up to 6 weeks to feel the full effect. You should speak with your healthcare provider before you stop taking NUPLAZID",
      },
    },
    {
      "@type": "Question",
      name: "What medicines might interact with NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Tell your healthcare provider about all the medicines you take. Other medicines may affect how NUPLAZID works. Some medicines should not be taken with NUPLAZID. Your healthcare provider can tell you if it is safe to take NUPLAZID with your other medicines. Do not start or stop any medicines while taking NUPLAZID without talking to your healthcare provider first",
      },
    },
    {
      "@type": "Question",
      name: "What warnings should I know about NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "QT Interval Prolongation: NUPLAZID may increase the risk of changes to your heart rhythm. This risk may increase if NUPLAZID is taken with certain other medicines known to prolong the QT interval. Tell your healthcare provider about all the medicines you take or have recently taken \n\nDo not take NUPLAZID if you have certain heart conditions that change your heart rhythm. It is important to talk to your healthcare provider about this possible side effect. Call your healthcare provider if you feel a change in your heartbeat \n\nFor additional safety information, see below (https://www.nuplazid.com/frequently-asked-questions#isi).",
      },
    },
    {
      "@type": "Question",
      name: "What are the possible side effects of NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The common side effects of NUPLAZID include \n-Swelling in the legs or arms \n-Nausea \n-Confusion \n-Hallucination \n-Constipation \n-Changes to normal walking \n\nThese are not all the possible side effects of NUPLAZID. For more information, ask your healthcare provider or pharmacist about this medicine.",
      },
    },
    {
      "@type": "Question",
      name: "How do I get NUPLAZID?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Acadia Connect® can coordinate with you and the specialty pharmacy filling the prescription to help get the medicine delivered wherever is most convenient to you \n\nLearn more about Acadia Connect (https://www.nuplazid.com/acadia-connect-patient-support).",
      },
    },
    {
      "@type": "Question",
      name: "How much does NUPLAZID cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "There are options available that may help you cover the cost of NUPLAZID. Visit Acadia Connect (https://www.nuplazid.com/acadia-connect-patient-support) to learn more",
      },
    },
  ],
};

export const Head: HeadFC = ({ location }) => (
  <Seo
    title="Frequently Asked Questions | NUPLAZID® (pimavanserin)"
    description="Answers to frequently asked questions about NUPLAZID® and Parkinson’s disease-related hallucinations and delusions. See Important Safety Information and full Prescribing Information including Boxed WARNING."
    pathname={location.pathname}
  >
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
    />
  </Seo>
);

export default FaqPage;
